import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import axios from 'axios';
import { API, USER_IMAGES } from '../../constants';

// Gallery Section with Before/After
export const GallerySection = () => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [activeFilter, setActiveFilter] = useState('all');
  const sliderRef = useRef(null);
  const isDragging = useRef(false);

  const handleMove = useCallback((clientX) => {
    if (!sliderRef.current || !isDragging.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e) => handleMove(e.clientX);
  const handleTouchMove = (e) => handleMove(e.touches[0].clientX);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseMove]);

  const [galleryItems, setGalleryItems] = useState([
    { src: USER_IMAGES.headlightBefore, label: 'Svetlomet pred renováciou', category: 'svetlomety' },
    { src: USER_IMAGES.headlightAfter, label: 'Svetlomet po renovácii', category: 'svetlomety' },
    { src: USER_IMAGES.drlLed, label: 'DRL LED pásky', category: 'svetlomety' },
    { src: USER_IMAGES.gallery1, label: 'Renovácia svetlometu', category: 'svetlomety' },
    { src: USER_IMAGES.gallery2, label: 'LED upgrade', category: 'svetlomety' },
    { src: USER_IMAGES.ambient, label: 'Ambient osvetlenie - Audi A4 B9', category: 'interiér', wide: true },
    { src: USER_IMAGES.ambientRed, label: 'Ambient osvetlenie - červené', category: 'interiér' },
    { src: USER_IMAGES.ambientBMW, label: 'Ambient osvetlenie - BMW červeno-modrý', category: 'interiér' },
    { src: USER_IMAGES.ambientVW, label: 'Ambient osvetlenie - VW', category: 'interiér' },
    { src: USER_IMAGES.starRoof1, label: 'Hviezdny strop - biely', category: 'interiér' },
    { src: USER_IMAGES.starRoof2, label: 'Hviezdny strop - biele hviezdy', category: 'interiér' },
    { src: USER_IMAGES.starRoof3, label: 'Hviezdny strop - fialový', category: 'interiér' },
    { src: USER_IMAGES.gallery3, label: 'Detailná práca', category: 'svetlomety' },
    { src: USER_IMAGES.gallery4, label: 'Výsledok renovácie', category: 'svetlomety' },
    { src: USER_IMAGES.gallery5, label: 'Profesionálny servis', category: 'svetlomety' },
    { src: USER_IMAGES.interior, label: 'Interiérové osvetlenie', category: 'interiér' },
  ]);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        if (res.data && res.data.length > 0) {
          setGalleryItems(res.data.map(item => ({ src: item.image_url, label: item.label, category: item.category, wide: item.wide })));
        }
      } catch (e) { /* keep fallback */ }
    };
    fetchGallery();
  }, []);

  const filters = [
    { id: 'all', label: 'Všetky' },
    { id: 'svetlomety', label: 'Svetlomety' },
    { id: 'interiér', label: 'Interiér' },
  ];

  const filtered = activeFilter === 'all' ? galleryItems : galleryItems.filter(i => i.category === activeFilter);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 bg-white/[0.02]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00BFFF] mb-4">
            Galéria
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium">
            Naša práca
          </h2>
          <p className="text-zinc-400 mt-4 max-w-2xl mx-auto">
            Pozrite si výsledky profesionálnej renovácie svetlometov a interiérových úprav
          </p>
        </div>

        {/* Before/After Slider */}
        <div 
          ref={sliderRef}
          data-testid="before-after-slider"
          className="relative w-full max-w-4xl mx-auto aspect-video overflow-hidden cursor-ew-resize mb-12"
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleMouseUp}
        >
          <img 
            src={USER_IMAGES.banner1}
            alt="Po renovácii"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div 
            className="absolute inset-0 overflow-hidden"
            style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
          >
            <img 
              src={USER_IMAGES.banner2}
              alt="Pred renováciou"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div 
            className="absolute top-0 bottom-0 w-1 bg-[#00BFFF] cursor-ew-resize z-10"
            style={{ left: `${sliderPosition}%` }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-[#00BFFF] rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(0,191,255,0.4)]">
              <div className="flex gap-0.5">
                <ChevronDown className="rotate-90 w-4 h-4 text-[#05050A]" />
                <ChevronDown className="-rotate-90 w-4 h-4 text-[#05050A]" />
              </div>
            </div>
          </div>
          <div className="absolute top-4 left-4 px-3 py-1 bg-black/50 text-sm font-medium">PRED</div>
          <div className="absolute top-4 right-4 px-3 py-1 bg-[#00BFFF]/50 text-sm font-medium">PO</div>
        </div>

        {/* Filter buttons */}
        <div className="flex justify-center gap-3 mb-8">
          {filters.map(f => (
            <button
              key={f.id}
              data-testid={`gallery-filter-${f.id}`}
              onClick={() => setActiveFilter(f.id)}
              className={`px-5 py-2 text-sm font-medium transition-all ${
                activeFilter === f.id
                  ? 'bg-[#00BFFF] text-[#05050A]'
                  : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {filtered.map((item, idx) => (
            <div 
              key={idx} 
              data-testid={`gallery-item-${idx}`}
              className={`group relative overflow-hidden ${item.wide ? 'col-span-2' : ''} aspect-video`}
            >
              <img 
                src={item.src}
                alt={item.label}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white text-sm font-medium">{item.label}</p>
                  <p className="text-[#00BFFF] text-xs uppercase tracking-wider mt-1">{item.category}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
